import type { DriveStep } from 'driver.js';

const at = (id: string) => `[data-tour="${id}"]`;

/**
 * The dashboard walkthrough — the ONE tour that auto-starts (once, on a user's first visit).
 * It tours the dashboard's OWN content first (what's right in front of them), then maps out the
 * sidebar's sections. Each destination page also has its own "Tour this page" button for a closer
 * look, so this stays a concise welcome rather than a deep dive into every page.
 *
 * Anchors: dash-* live in DashboardPage.tsx; sidebar / studio / content / golive / account /
 * tour-trigger live in AgentLayout.tsx (the sidebar group headers + topbar button, always visible
 * on desktop). On mobile the sidebar is behind the hamburger, so those steps are simply skipped
 * (runTour drops anchors that aren't in the DOM).
 */
export function getDashboardTourSteps(): DriveStep[] {
  return [
    {
      popover: {
        title: '👋 Welcome to your dashboard',
        description:
          "Here's a quick tour of your dashboard and how your menu is organized. You can skip " +
          'anytime, and replay it later from the “Take a tour” button.',
      },
    },
    {
      element: at('dash-ai-prompt'),
      popover: {
        title: 'Build with AI',
        description:
          'The fastest way to start: describe the site you want in plain English (or tap the mic) ' +
          'and the AI builds it for you.',
        side: 'bottom',
        align: 'center',
      },
    },
    {
      element: at('dash-setup'),
      popover: {
        title: 'Finish setting up',
        description:
          'A checklist of what your site still needs. Click an item to go and do it, or let the AI ' +
          'set up the rest for you: your bio, community pages, starter blog posts and your AI ' +
          'Marketer’s voice.',
        side: 'bottom',
        align: 'center',
      },
    },
    {
      element: at('dash-template-gallery'),
      popover: {
        title: 'Or start from a template',
        description: 'Prefer a head start? Pick a ready-made template and make it your own.',
        side: 'top',
        align: 'center',
      },
    },
    {
      element: at('dash-stats'),
      popover: {
        title: 'Track your results',
        description:
          'Once your site is live, your visits, new leads, and top-performing page show up here at a ' +
          'glance.',
        side: 'top',
        align: 'center',
      },
    },
    {
      element: at('dash-recent-leads'),
      popover: {
        title: 'Follow up with leads',
        description: 'New inquiries from your site land here so you can reach out fast.',
        side: 'top',
        align: 'center',
      },
    },
    {
      element: at('sidebar'),
      popover: {
        title: 'Your menu',
        description: 'Everything lives in this menu, grouped into sections. Here’s a quick map.',
        side: 'right',
        align: 'start',
      },
    },
    {
      element: at('sites'),
      popover: {
        title: 'Your site',
        description: 'The site you are working on. Click it to see every site on your account, add one or switch.',
        side: 'right',
        align: 'start',
      },
    },
    {
      element: at('nav-leads'),
      popover: {
        title: 'Leads',
        description: 'Every inquiry from your site, with a count of the new ones waiting for you.',
        side: 'right',
        align: 'start',
      },
    },
    {
      element: at('marketer'),
      popover: {
        title: 'Grow: AI Marketer',
        description:
          'Home valuations that capture sellers, a scored hot list of leads, and a monthly email to ' +
          'your farm. Included on Brokerage Growth, an add-on on Agent IDX Growth.',
        side: 'right',
        align: 'start',
      },
    },
    {
      element: at('autopilot'),
      popover: {
        title: 'Grow: SEO Autopilot',
        description:
          'Keeps working on your site for Google: daily fixes, a weekly blog post and a monthly ' +
          'report. Nothing goes live without you unless you say so.',
        side: 'right',
        align: 'start',
      },
    },
    {
      element: at('studio'),
      popover: {
        title: 'Website: Edit my website',
        description: 'Open the builder anytime to create and edit your pages with AI. Preview is a button inside it.',
        side: 'right',
        align: 'start',
      },
    },
    {
      element: at('design'),
      popover: {
        title: 'Look and brand',
        description:
          'Pick a Look changes the layout of your home, search and listing pages. Card Style changes ' +
          'how listing and agent cards look. Brand Manager holds your logos, colors and fonts.',
        side: 'right',
        align: 'start',
      },
    },
    {
      element: at('content'),
      popover: {
        title: 'Content',
        description: 'Your blog, communities, listings you enter yourself, agents and offices.',
        side: 'right',
        align: 'start',
      },
    },
    {
      element: at('golive'),
      popover: {
        title: 'Go Live',
        description: 'Publish your site and set up your domain, all under Go Live.',
        side: 'right',
        align: 'start',
      },
    },
    {
      element: at('account'),
      popover: {
        title: 'Account',
        description: 'Your profile, team, connections (MLS, reviews, CRM, API keys), plan and billing with AI usage, and settings.',
        side: 'right',
        align: 'start',
      },
    },
    {
      element: at('help'),
      popover: {
        title: 'Help',
        description:
          'Step-by-step guides for every feature. Click Help on any page and the guide for that page ' +
          'comes up first.',
        side: 'bottom',
        align: 'end',
      },
    },
    {
      element: at('tour-trigger'),
      popover: {
        title: "That's the tour! 🎉",
        description:
          'Replay this anytime from here. And every page has its own “Tour this page” button for a ' +
          'closer look at that page’s features.',
        side: 'bottom',
        align: 'end',
      },
    },
  ];
}
